/*
 * Engraving geometry — shared between the engraving preview (theme/admin)
 * and the SVG generator (backend/app/controllers/orders.controller.js).
 *
 * SOURCE OF TRUTH: backend `returnZodiacSvg` / `returnSvg`. Any change to
 * constants or formulas here MUST be mirrored in the backend, and vice versa.
 *
 * All values returned are in viewBox units (BOX_W x BOX_H). Callers map
 * to CSS pixels using the rendered bounding-box height.
 */
(function (root) {
  'use strict';

  var BOX_W = 148;
  var BOX_H = 174;
  var MARGIN_TOP = 0;
  var SYMBOL_TEXT_GAP = 40;
  // Vertical gap added on top of fontSize when stacking lines, and reused as
  // the bottom-of-box padding so the last descender stays inside. Set to 4 to
  // mirror text-mode line spacing (29 units between baselines at fs=25),
  // which also pushes line 1 a bit lower vs. the older value of 6.
  var LINE_SPACING_OFFSET = 4;
  var TEXT_FONT_SCALE = 0.8;   // text-mode SVG font scaling
  // Fraction of fontSize occupied by visible glyph height above baseline
  // (cap height). 0.7 tracks typical font metrics.
  var TEXT_ASCENT_RATIO = 0.7;
  // Always-on visible breathing room between symbol bottom and text cap top.
  // Without this the symbol stops right at the cap line, which reads as
  // "touching". Applied to both preview and SVG.
  var SYMBOL_TEXT_VISUAL_GAP = 6;
  // Symbol's max-height calculation is anchored to this reference font size
  // so that the symbol slider and the font-size slider work independently:
  // changing the user's fontSize moves the rendered text but does NOT shrink
  // or grow the symbol. Default 25 — same as the slider's default.
  var REFERENCE_FONT_SIZE = 25;

  // Six text-mode line Y positions (matches returnSvg)
  var TEXT_MODE_LINE_Y = [20.3, 49.3, 78.3, 107.3, 136.3, 165.3];
  var TEXT_MODE_X = 74;

  function clampSize(size) {
    var n = parseFloat(size);
    if (!isFinite(n)) n = 0;
    return Math.min(100, Math.max(0, n));
  }

  /*
   * Compute zodiac/symbol-mode layout in viewBox units.
   *
   * Inputs:
   *   size         - 0..100 slider value (% of available symbol height)
   *   fontSize     - text font size in viewBox units (matches SVG)
   *   lineOne      - first text line (any truthy => content present)
   *   lineTwo      - second text line
   *   zodiac       - selected symbol key (used for SAFETY_SCALE)
   *   mode         - 'zodiac' | 'xmas' | 'valentines' | 'easter' | 'teachers'
   *   reserveLines - optional fixed reservation count (0..2). When provided,
   *                  overrides content-based counting so the symbol size does
   *                  NOT change as the user types into text inputs. The slider
   *                  alone determines symbol size; overflow is the user's
   *                  responsibility to resolve.
   *   blankLines   - optional count of empty lines of vertical breathing room
   *                  between symbol bottom and text cap top, in addition to
   *                  the always-on SYMBOL_TEXT_VISUAL_GAP. Useful for the
   *                  preview where extra spacing helps the user see symbol
   *                  and text as separate elements. Default 0.
   *
   * Returns: { boxW, boxH, symbolTop, symbolHeight, textLine1Y, textLine2Y, fontSize }
   */
  function computeZodiacLayout(opts) {
    opts = opts || {};
    var size = clampSize(opts.size);
    var fs = parseFloat(opts.fontSize);
    if (!isFinite(fs) || fs <= 0) fs = 25;

    var numTextLines;
    if (typeof opts.reserveLines === 'number' && isFinite(opts.reserveLines)) {
      numTextLines = Math.max(0, Math.min(2, Math.round(opts.reserveLines)));
    } else {
      var has1 = !!(opts.lineOne && String(opts.lineOne).trim());
      var has2 = !!(opts.lineTwo && String(opts.lineTwo).trim());
      numTextLines = (has1 ? 1 : 0) + (has2 ? 1 : 0);
    }

    var lineHeight = fs + LINE_SPACING_OFFSET;

    // Text positions are FIXED — anchored to the bottom of the box. They do
    // NOT move when the user resizes the symbol. Last line's baseline sits
    // LINE_SPACING_OFFSET above the box bottom so its descender stays inside;
    // line 1 stacks above. Same model as text mode.
    var textLine1Y, textLine2Y;
    if (numTextLines === 0) {
      textLine1Y = BOX_H + 1; // off-canvas — no text rendered
      textLine2Y = BOX_H + 1;
    } else if (numTextLines === 1) {
      textLine1Y = BOX_H - LINE_SPACING_OFFSET;
      textLine2Y = textLine1Y + lineHeight; // off-box; unused
    } else {
      textLine2Y = BOX_H - LINE_SPACING_OFFSET;
      textLine1Y = textLine2Y - lineHeight;
    }

    // Slider value maps directly to symbol height in viewBox units (1:1).
    // size=80 → 80 units tall. If symbol + text don't fit, the overflow
    // check surfaces it and the user adjusts size, font, or text length.
    var symbolHeight = size;
    if (symbolHeight < 0) symbolHeight = 0;

    return {
      boxW: BOX_W,
      boxH: BOX_H,
      symbolTop: MARGIN_TOP,
      symbolHeight: symbolHeight,
      textLine1Y: textLine1Y,
      textLine2Y: textLine2Y,
      fontSize: fs
    };
  }

  /*
   * Compute text-mode layout (6 lines).
   * fontSize here is the slider value; SVG renders at fontSize * TEXT_FONT_SCALE.
   */
  function computeTextLayout(opts) {
    opts = opts || {};
    var fs = parseFloat(opts.fontSize);
    if (!isFinite(fs) || fs <= 0) fs = 25;
    return {
      boxW: BOX_W,
      boxH: BOX_H,
      x: TEXT_MODE_X,
      lineYs: TEXT_MODE_LINE_Y.slice(),
      scaledFontSize: Math.round(fs * TEXT_FONT_SCALE),
      rawFontSize: fs
    };
  }

  var api = {
    BOX_W: BOX_W,
    BOX_H: BOX_H,
    MARGIN_TOP: MARGIN_TOP,
    SYMBOL_TEXT_GAP: SYMBOL_TEXT_GAP,
    LINE_SPACING_OFFSET: LINE_SPACING_OFFSET,
    TEXT_FONT_SCALE: TEXT_FONT_SCALE,
    TEXT_ASCENT_RATIO: TEXT_ASCENT_RATIO,
    TEXT_MODE_LINE_Y: TEXT_MODE_LINE_Y,
    TEXT_MODE_X: TEXT_MODE_X,
    clampSize: clampSize,
    computeZodiacLayout: computeZodiacLayout,
    computeTextLayout: computeTextLayout
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.EngravingGeometry = api;
  }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
